import { useNavigate } from '@tanstack/react-router';
import type { SessionRow, EhrRow } from '../../lib/api.js';

function statusStyle(status: string): { bg: string; color: string; border: string } {
  if (status === 'completed') return { bg: 'var(--accent-green)22', color: 'var(--accent-green)', border: 'var(--accent-green)55' };
  if (status === 'failed') return { bg: 'var(--accent-red)22', color: 'var(--accent-red)', border: 'var(--accent-red)55' };
  return { bg: 'var(--text-dimmed)22', color: 'var(--text-dimmed)', border: 'var(--text-dimmed)55' };
}

function duration(startedAt: string | null, finishedAt: string | null): string {
  if (!startedAt || !finishedAt) return '—';
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  const min = Math.floor(ms / 60000);
  const sec = Math.floor((ms % 60000) / 1000);
  return min > 0 ? `${min}m ${sec}s` : `${sec}s`;
}

export function RecentSessionsTable({ sessions, ehrs }: { sessions: SessionRow[]; ehrs: EhrRow[] }) {
  const navigate = useNavigate();

  const recent = sessions
    .filter((s) => s.status === 'completed' || s.status === 'failed')
    .slice()
    .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))
    .slice(0, 15);

  if (recent.length === 0) {
    return (
      <div className="text-sm text-text-dimmed py-4 text-center">No completed sessions yet</div>
    );
  }

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border">
          <th className="pb-2 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Session</th>
          <th className="pb-2 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">EHR</th>
          <th className="pb-2 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Status</th>
          <th className="pb-2 text-right text-xs font-semibold uppercase tracking-wider text-text-secondary">Duration</th>
          <th className="pb-2 text-right text-xs font-semibold uppercase tracking-wider text-text-secondary">Started</th>
        </tr>
      </thead>
      <tbody>
        {recent.map((s) => {
          const st = statusStyle(s.status);
          const ehrSource = s.sources.find((src) => src.kind === 'ehr');
          const ehr = ehrs.find((e) => e.id === ehrSource?.ehrId || e.slug === ehrSource?.ehrSlug);
          return (
            <tr
              key={s.id}
              className="border-b border-border last:border-0 cursor-pointer transition-colors"
              style={s.status === 'failed' ? { background: 'rgba(238,93,80,0.04)' } : undefined}
              onClick={() => void navigate({ to: '/sessions/$id', params: { id: s.id } })}
            >
              <td className="py-2.5 pr-4">
                <div className="font-semibold text-text-primary truncate max-w-[180px]">{s.title ?? s.id.slice(0, 8)}</div>
              </td>
              <td className="py-2.5 pr-4 text-xs text-text-secondary">{ehr?.displayName ?? '—'}</td>
              <td className="py-2.5 pr-4">
                <span
                  className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase"
                  style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}` }}
                >
                  {s.status}
                </span>
              </td>
              <td className="py-2.5 pr-4 text-right font-mono text-xs text-text-dimmed">
                {duration(s.startedAt, s.finishedAt)}
              </td>
              <td className="py-2.5 text-right font-mono text-xs text-text-dimmed">
                {s.startedAt ? new Date(s.startedAt).toLocaleString() : '—'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
